import React, { useState, useContext, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";

import {
  Button,
  CssBaseline,
  TextField,
  Grid,
  Container,
  InputAdornment,
  IconButton,
  Link,
  Typography,
  CircularProgress,
  Paper
} from '@material-ui/core';

import { Visibility, VisibilityOff, EmailOutlined, LockOutlined } from '@material-ui/icons';
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#31357b',
    padding: theme.spacing(1),
  },
  paper: {
    padding: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(5),
    },
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderRadius: "10px",
    boxShadow: "0 15px 35px rgba(0,0,0,0.2)",
    backgroundColor: "rgba(255, 255, 255, 0.98)",
    width: '100%',
  },
  logo: {
    width: "120px",
    [theme.breakpoints.up('sm')]: {
      width: "160px",
    },
    marginBottom: theme.spacing(2),
    height: 'auto',
  },
  form: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
    },
    marginBottom: theme.spacing(0.5),
  },
  submit: {
    margin: theme.spacing(2, 0, 2),
    padding: theme.spacing(1),
    borderRadius: "8px",
    fontWeight: "bold",
    textTransform: "none",
    fontSize: "1rem",
    background: "#0035a2",
    color: '#fff'
  },
  footerText: {
    color: 'white',
    marginTop: theme.spacing(3),
    textAlign: 'center',
    fontSize: '0.85rem',
    opacity: 0.8,
  }
}));

const Login = () => {
  const classes = useStyles();
  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { handleLogin } = useContext(AuthContext);

  const handleChangeInput = useCallback((e) => {
    const { name, value } = e.target;
    setUser(prev => ({ ...prev, [name]: value.trim()}));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await handleLogin(user);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <CssBaseline />
      <Container component="main" maxWidth="xs" disableGutters>
        <Paper elevation={0} className={classes.paper}>
          <img
            src="https://www.nrwork.com.br/conteudo/imagem/e44315dff97ddb544b2c523e3fc64b71_g.png"
            alt="NR Work Logo"
            className={classes.logo}
          />

          <Typography variant="body2" color="textSecondary" align="center" style={{ marginBottom: 20 }}>
            Digite seus dados para entrar no sistema
          </Typography>

          <form className={classes.form} noValidate onSubmit={handleSubmit}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              size="medium"

              id="email"
              label={i18n.t("login.form.email")}
              name="email"
              value={user.email}
              onChange={handleChangeInput}
              autoComplete="email"
              autoFocus
              className={classes.textField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              size="medium"
              name="password"
              label={i18n.t("login.form.password")}
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={user.password}
              onChange={handleChangeInput}
              autoComplete="current-password"
              className={classes.textField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Button
              type="submit"
              size="small"
              fullWidth
              variant="contained"
              className={classes.submit}
              disableElevation
            >
              {i18n.t("login.buttons.submit")}
            </Button>

            <Grid container>
              <Grid item xs={12} style={{ textAlign: 'center' }}>
                <Link
                  component={RouterLink}
                  to="/signup"
                  variant="body2"
                  style={{ fontWeight: 600, textDecoration: 'none' }}
                >
                  {i18n.t("login.buttons.register")}
                </Link>
              </Grid>
            </Grid>
          </form>
        </Paper>

        <Typography className={classes.footerText}>
          © {new Date().getFullYear()} NR Work - Todos os direitos reservados.
        </Typography>
      </Container>
    </div>
  );
};

export default Login;